import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { 
  Globe, 
  CheckCircle2, 
  XCircle, 
  RefreshCw, 
  Save,
  AlertTriangle,
  Loader2,
  GraduationCap,
  BookOpen,
  Settings,
  Clock,
  Calendar,
  Play,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Trophy,
  Eye,
  Bell,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import AppLayout from '../components/AppLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WixIntegrationPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResults, setBackfillResults] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [mappings, setMappings] = useState(null);
  const [wixPrograms, setWixPrograms] = useState(null);
  
  // Sync History state
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncConfig, setSyncConfig] = useState(null);
  const [manualSyncRunning, setManualSyncRunning] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState({});
  
  // Completion Detection state (Phase 6)
  const [completionSettings, setCompletionSettings] = useState({
    enabled: true,
    auto_create_case_note: true,
    notify_case_manager: true,
    notification_recipients: []
  });
  const [completionSettingsLoading, setCompletionSettingsLoading] = useState(false);
  const [completions, setCompletions] = useState(null);
  const [completionsLoading, setCompletionsLoading] = useState(false);
  const [completionFilters, setCompletionFilters] = useState({
    start_date: '',
    end_date: ''
  });
  const [expandedCourses, setExpandedCourses] = useState({});

  useEffect(() => {
    // Small delay to ensure token is available after auth redirect
    const timer = setTimeout(() => {
      fetchData();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('myhelp_token');
      if (!token) {
        console.log('No token found, waiting...');
        // Retry after a short delay
        setTimeout(fetchData, 500);
        return;
      }
      const [statusRes, mappingsRes, syncHistoryRes, syncConfigRes] = await Promise.all([
        axios.get(`${API}/admin/wix-integration/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/wix-integration/program-mappings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/wix-integration/sync-history`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { logs: [] } })),
        axios.get(`${API}/admin/wix-integration/sync-config`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      setConnectionStatus(statusRes.data);
      setMappings(mappingsRes.data);
      setSyncHistory(syncHistoryRes.data.logs || []);
      setSyncConfig(syncConfigRes.data);
      
      // Fetch completion detection settings
      try {
        const completionSettingsRes = await axios.get(`${API}/admin/wix-integration/completion-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCompletionSettings(completionSettingsRes.data);
      } catch (err) {
        console.log('Completion settings not available or access denied');
      }
    } catch (error) {
      console.error('Error fetching Wix integration data:', error);
      toast.error('Failed to load Wix integration settings');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    try {
      setTesting(true);
      const token = localStorage.getItem('myhelp_token');
      const response = await axios.get(`${API}/admin/wix-integration/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnectionStatus(response.data);
      
      if (response.data.connection?.connected) {
        toast.success('Wix API connection successful');
      } else {
        toast.error(response.data.connection?.error || 'Connection failed');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error('Failed to test connection');
    } finally {
      setTesting(false);
    }
  };

  const handleMappingChange = (programType, index, field, value) => {
    setMappings(prev => {
      const updated = { ...prev };
      if (!updated[programType]) return prev;
      updated[programType] = [...updated[programType]];
      updated[programType][index] = {
        ...updated[programType][index],
        [field]: value
      };
      return updated;
    });
  };

  const saveMappings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('myhelp_token');
      await axios.put(`${API}/admin/wix-integration/program-mappings`, mappings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Program mappings saved successfully');
    } catch (error) {
      console.error('Error saving mappings:', error);
      toast.error('Failed to save mappings');
    } finally {
      setSaving(false);
    }
  };

  const refreshFromWix = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('myhelp_token');
      const response = await axios.get(`${API}/admin/wix-integration/fetch-programs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      setWixPrograms(data);
      
      if (data.success && data.total_programs > 0) {
        // Update mappings while preserving existing is_active states
        setMappings(prev => {
          // Create lookup maps for existing programs by wix_program_id
          const existingPathwaysMap = new Map(
            (prev?.pathways_to_employability || []).map(p => [p.wix_program_id, p])
          );
          const existingHelpMap = new Map(
            (prev?.help_institute || []).map(p => [p.wix_program_id, p])
          );
          
          // Merge new data with existing active states
          const mergedPathways = data.pathways_to_employability.map(newProgram => {
            const existing = existingPathwaysMap.get(newProgram.wix_program_id);
            return {
              ...newProgram,
              // Preserve existing is_active, default new programs to false (inactive)
              is_active: existing?.is_active ?? false
            };
          });
          
          const mergedHelpInstitute = data.help_institute.map(newProgram => {
            const existing = existingHelpMap.get(newProgram.wix_program_id);
            return {
              ...newProgram,
              // Preserve existing is_active, default new programs to false (inactive)
              is_active: existing?.is_active ?? false
            };
          });
          
          return {
            ...prev,
            pathways_to_employability: mergedPathways,
            help_institute: mergedHelpInstitute,
            fetched_from_wix: true,
            fetched_at: data.fetched_at
          };
        });
        toast.success(`Fetched ${data.total_programs} programs from Wix`);
      } else {
        toast.warning(data.message || 'No programs found in Wix');
      }
    } catch (error) {
      console.error('Error fetching programs from Wix:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to fetch programs from Wix';
      toast.error(errorMsg);
    } finally {
      setRefreshing(false);
    }
  };

  const runBackfill = async () => {
    try {
      setBackfillRunning(true);
      setBackfillResults(null);
      const token = localStorage.getItem('myhelp_token');
      const response = await axios.post(`${API}/admin/wix-integration/backfill`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBackfillResults(response.data);
      
      const { matched_and_updated, already_matched, not_in_wix, no_email, errors } = response.data;
      if (matched_and_updated?.length > 0) {
        toast.success(`Backfill complete: ${matched_and_updated.length} records matched and updated`);
      } else if (already_matched?.length > 0) {
        toast.info(`Backfill complete: All ${already_matched.length} records were already matched`);
      } else if (not_in_wix?.length > 0 || no_email?.length > 0 || errors?.length > 0) {
        toast.warning(`Backfill complete with issues. See results below.`);
      } else {
        toast.info('Backfill complete: No records to process');
      }
    } catch (error) {
      console.error('Error running backfill:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to run backfill';
      toast.error(errorMsg);
    } finally {
      setBackfillRunning(false);
    }
  };

  const triggerManualSync = async () => {
    try {
      setManualSyncRunning(true);
      const token = localStorage.getItem('myhelp_token');
      await axios.post(`${API}/admin/wix-integration/manual-sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Manual sync initiated. Check history for results.');
      
      // Refresh sync history after a short delay
      setTimeout(async () => {
        const historyRes = await axios.get(`${API}/admin/wix-integration/sync-history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSyncHistory(historyRes.data.logs || []);
      }, 3000);
    } catch (error) {
      console.error('Error triggering manual sync:', error);
      toast.error(error.response?.data?.detail || 'Failed to trigger manual sync');
    } finally {
      setManualSyncRunning(false);
    }
  };

  const toggleLogExpanded = (logId) => {
    setExpandedLogs(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const formatSyncDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  // Completion Detection functions (Phase 6)
  const saveCompletionSettings = async () => {
    try {
      setCompletionSettingsLoading(true);
      const token = localStorage.getItem('myhelp_token');
      await axios.put(`${API}/admin/wix-integration/completion-settings`, completionSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Completion detection settings saved');
    } catch (error) {
      console.error('Error saving completion settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save completion settings');
    } finally {
      setCompletionSettingsLoading(false);
    }
  };

  const fetchCompletions = async () => {
    if (!completionFilters.start_date || !completionFilters.end_date) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    try {
      setCompletionsLoading(true);
      const token = localStorage.getItem('myhelp_token');
      const params = new URLSearchParams();
      params.append('start_date', completionFilters.start_date);
      params.append('end_date', completionFilters.end_date);
      
      const response = await axios.get(`${API}/admin/wix-integration/completions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletions(response.data);
    } catch (error) {
      console.error('Error fetching completions:', error);
      toast.error(error.response?.data?.detail || 'Failed to fetch completions');
    } finally {
      setCompletionsLoading(false);
    }
  };

  const toggleCourseExpanded = (courseName) => {
    setExpandedCourses(prev => ({
      ...prev,
      [courseName]: !prev[courseName]
    }));
  };

  const exportCompletionsCsv = () => {
    if (!completions) return;
    
    const headers = ['Course', 'Participant Name', 'Completion Date', 'Case ID', 'Detected At'];
    const rows = completions.completions.map(comp => [
      comp.course_name,
      comp.participant_name,
      comp.completion_date,
      comp.case_id,
      new Date(comp.detected_at).toLocaleString()
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Wix_Course_Completions_${completionFilters.start_date}_to_${completionFilters.end_date}.csv`;
    link.click();
    toast.success('Completions exported to CSV');
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Globe className="h-6 w-6 text-primary" />
              Wix Online Programs
            </h1>
            <p className="text-muted-foreground mt-1">
              Configure Wix API connection and program mappings for SNAP E&T
            </p>
          </div>
        </div>

        {/* Connection Status Card */}
        <Card data-testid="wix-connection-status-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Connection Status
            </CardTitle>
            <CardDescription>
              Wix API connection configuration and status
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* App ID Status */}
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                {connectionStatus?.has_app_id ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
                <div>
                  <p className="font-medium text-sm">App ID</p>
                  <p className="text-xs text-muted-foreground">
                    {connectionStatus?.has_app_id ? 'Configured' : 'Not Configured'}
                  </p>
                </div>
              </div>

              {/* App Secret Status */}
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                {connectionStatus?.has_app_secret ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
                <div>
                  <p className="font-medium text-sm">App Secret</p>
                  <p className="text-xs text-muted-foreground">
                    {connectionStatus?.has_app_secret ? 'Configured' : 'Not Configured'}
                  </p>
                </div>
              </div>

              {/* Instance ID Status */}
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                {connectionStatus?.has_instance_id ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
                <div>
                  <p className="font-medium text-sm">Instance ID</p>
                  <p className="text-xs text-muted-foreground">
                    {connectionStatus?.has_instance_id ? 'Configured' : 'Not Configured'}
                  </p>
                </div>
              </div>

              {/* Connection Status */}
              <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                {connectionStatus?.connection?.connected ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                )}
                <div>
                  <p className="font-medium text-sm">Connection</p>
                  <p className="text-xs text-muted-foreground">
                    {connectionStatus?.connection?.connected ? 'Connected' : 'Not Connected'}
                  </p>
                </div>
              </div>
            </div>

            {/* OAuth Status */}
            {connectionStatus?.connection?.oauth !== undefined && (
              <div className={`p-3 rounded-lg border ${
                connectionStatus.connection.oauth 
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              }`}>
                <div className="flex items-center gap-2">
                  {connectionStatus.connection.oauth ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                  <p className={`text-sm font-medium ${
                    connectionStatus.connection.oauth 
                      ? 'text-green-700 dark:text-green-400' 
                      : 'text-red-700 dark:text-red-400'
                  }`}>
                    OAuth Authentication: {connectionStatus.connection.oauth_status || 'Unknown'}
                  </p>
                </div>
                {connectionStatus.connection.oauth_error && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1 ml-6">
                    {connectionStatus.connection.oauth_error}
                  </p>
                )}
              </div>
            )}

            {/* API Status Details */}
            {connectionStatus?.connection && (
              <div className="space-y-3">
                {/* Members API Status */}
                <div className={`p-3 rounded-lg border ${
                  connectionStatus.connection.members_api 
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                }`}>
                  <div className="flex items-center gap-2">
                    {connectionStatus.connection.members_api ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <p className={`text-sm font-medium ${
                      connectionStatus.connection.members_api 
                        ? 'text-green-700 dark:text-green-400' 
                        : 'text-red-700 dark:text-red-400'
                    }`}>
                      Members API: {connectionStatus.connection.members_api_status || 'Unknown'}
                      {connectionStatus.connection.members_api && connectionStatus.connection.members_count !== undefined && (
                        <span className="font-normal"> ({connectionStatus.connection.members_count} member{connectionStatus.connection.members_count !== 1 ? 's' : ''} found)</span>
                      )}
                    </p>
                  </div>
                  {connectionStatus.connection.members_api_error && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1 ml-6">
                      {connectionStatus.connection.members_api_error}
                    </p>
                  )}
                  {connectionStatus.connection.members_api_help && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1 ml-6">
                      {connectionStatus.connection.members_api_help}
                    </p>
                  )}
                </div>

                {/* Online Programs API Status */}
                <div className={`p-3 rounded-lg border ${
                  connectionStatus.connection.online_programs_api 
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                    : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                }`}>
                  <div className="flex items-center gap-2">
                    {connectionStatus.connection.online_programs_api ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                    )}
                    <p className={`text-sm font-medium ${
                      connectionStatus.connection.online_programs_api 
                        ? 'text-green-700 dark:text-green-400' 
                        : 'text-amber-700 dark:text-amber-400'
                    }`}>
                      Online Programs API: {connectionStatus.connection.online_programs_api_status || 'Unknown'}
                      {connectionStatus.connection.online_programs_api && connectionStatus.connection.programs_count !== undefined && (
                        <span className="font-normal"> ({connectionStatus.connection.programs_count} program{connectionStatus.connection.programs_count !== 1 ? 's' : ''} found)</span>
                      )}
                    </p>
                  </div>
                  {connectionStatus.connection.online_programs_api_error && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 ml-6">
                      {connectionStatus.connection.online_programs_api_error}
                    </p>
                  )}
                  {connectionStatus.connection.online_programs_api_help && (
                    <div className="mt-2 ml-6 p-2 bg-amber-100 dark:bg-amber-900/40 rounded text-xs text-amber-700 dark:text-amber-300">
                      <strong>How to fix:</strong> {connectionStatus.connection.online_programs_api_help}
                    </div>
                  )}
                </div>

                {/* Overall Status */}
                {connectionStatus.connection.fully_connected && (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <p className="text-sm font-medium text-green-700 dark:text-green-400">
                        Fully Connected - All Wix APIs are accessible
                      </p>
                    </div>
                  </div>
                )}
                {connectionStatus.connection.warning && !connectionStatus.connection.fully_connected && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        {connectionStatus.connection.warning}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Connection Error */}
            {connectionStatus?.connection?.error && !connectionStatus?.connection?.connected && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-700 dark:text-red-400">
                  <strong>Error:</strong> {connectionStatus.connection.error}
                </p>
              </div>
            )}

            {/* Configuration Instructions */}
            {!connectionStatus?.configured && (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <h4 className="font-medium text-amber-800 dark:text-amber-300 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Configuration Required
                </h4>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                  To connect to Wix, add the following environment variables to the backend:
                </p>
                <ul className="text-sm text-amber-700 dark:text-amber-400 mt-2 list-disc list-inside space-y-1">
                  <li><code className="bg-amber-100 dark:bg-amber-900/50 px-1 rounded">WIX_APP_ID</code> - Your Wix App ID (required)</li>
                  <li><code className="bg-amber-100 dark:bg-amber-900/50 px-1 rounded">WIX_APP_SECRET</code> - Your Wix App Secret (required)</li>
                  <li><code className="bg-amber-100 dark:bg-amber-900/50 px-1 rounded">WIX_INSTANCE_ID</code> - App Installation Instance ID (required)</li>
                </ul>
                <div className="text-xs text-amber-600 dark:text-amber-500 mt-3 space-y-1">
                  <p>
                    <strong>Get App Credentials:</strong> <a href="https://dev.wix.com/apps" target="_blank" rel="noopener noreferrer" className="underline">Wix Developers Center</a> → Your App → OAuth
                  </p>
                  <p>
                    <strong>Get Instance ID:</strong> In Wix Developers → Your App → Test Your App → Copy the Instance ID from the test site installation
                  </p>
                  <p>
                    <strong>Note:</strong> The Instance ID is NOT the Site ID. It's a unique ID generated when the app is installed on a specific site.
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={testConnection}
                disabled={testing || !connectionStatus?.configured}
                data-testid="test-wix-connection-btn"
              >
                {testing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Test Connection
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Program Mappings */}
        <Card data-testid="wix-program-mappings-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" />
                  Program Mappings
                </CardTitle>
                <CardDescription>
                  Programs are automatically fetched from Wix and categorized. Click Refresh to update.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={refreshFromWix}
                disabled={refreshing || !connectionStatus?.connection?.online_programs_api}
                data-testid="refresh-wix-programs-btn"
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh from Wix
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Wix API Permission Warning */}
            {connectionStatus?.connection?.connected && !connectionStatus?.connection?.online_programs_api && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                  <div className="text-sm text-amber-700 dark:text-amber-400">
                    <strong>Online Programs API not accessible.</strong> To auto-populate programs from Wix, 
                    ensure your Wix App has "Online Programs" permissions enabled.
                  </div>
                </div>
              </div>
            )}

            {/* Fetch Status Banner */}
            {wixPrograms && (
              <div className={`mb-4 p-3 rounded-lg border ${
                wixPrograms.total_programs > 0 
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                  : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
              }`}>
                <div className="flex items-center gap-2 text-sm">
                  {wixPrograms.total_programs > 0 ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                  )}
                  <span className={wixPrograms.total_programs > 0 ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}>
                    {wixPrograms.message}
                    {wixPrograms.fetched_at && (
                      <span className="text-muted-foreground ml-2">
                        (Last refresh: {new Date(wixPrograms.fetched_at).toLocaleString()})
                      </span>
                    )}
                  </span>
                </div>
              </div>
            )}

            <Tabs defaultValue="pathways" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="pathways" className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Pathways to Employability
                  {mappings?.pathways_to_employability?.length > 0 && (
                    <Badge variant="secondary" className="ml-1">
                      {mappings.pathways_to_employability.filter(p => p.is_active).length}/{mappings.pathways_to_employability.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="help_institute" className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4" />
                  HELP Institute
                  {mappings?.help_institute?.length > 0 && (
                    <Badge variant="secondary" className="ml-1">
                      {mappings.help_institute.filter(p => p.is_active).length}/{mappings.help_institute.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Pathways to Employability Modules */}
              <TabsContent value="pathways" className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Programs with titles starting with "M" followed by a number (e.g., "M1: Building a Foundation") are automatically categorized as Pathways to Employability modules. Only <strong>Active</strong> modules are available for participant enrollment.
                </p>
                {mappings?.pathways_to_employability?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No Pathways modules found. Click "Refresh from Wix" to fetch programs.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-20">Active</TableHead>
                        <TableHead className="w-20">Module</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead className="w-80">Wix Program ID</TableHead>
                        <TableHead className="w-20">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mappings?.pathways_to_employability?.map((module, index) => (
                        <TableRow key={module.module_number || index} className={`hover:bg-muted/50 transition-colors ${!module.is_active ? 'opacity-60' : ''}`}>
                          <TableCell>
                            <Switch
                              checked={module.is_active || false}
                              onCheckedChange={(checked) => handleMappingChange('pathways_to_employability', index, 'is_active', checked)}
                              data-testid={`pathways-module-${module.module_number}-active`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            <Badge variant="outline">M{module.module_number}</Badge>
                          </TableCell>
                          <TableCell>
                            <div>
                              <span className="font-medium">{module.name}</span>
                              {module.description && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{module.description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              placeholder="Enter Wix Program ID"
                              value={module.wix_program_id || ''}
                              onChange={(e) => handleMappingChange('pathways_to_employability', index, 'wix_program_id', e.target.value)}
                              className="font-mono text-xs"
                              data-testid={`pathways-module-${module.module_number}-wix-id`}
                            />
                          </TableCell>
                          <TableCell>
                            {module.wix_program_id ? (
                              <Badge variant="default" className="bg-green-600">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Linked
                              </Badge>
                            ) : (
                              <Badge variant="secondary">Pending</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>

              {/* HELP Institute Courses */}
              <TabsContent value="help_institute" className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  All programs not starting with "M" followed by a number are automatically categorized as The HELP Institute courses. Only <strong>Active</strong> courses are available for participant enrollment.
                </p>
                {mappings?.help_institute?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <GraduationCap className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No HELP Institute courses found. Click "Refresh from Wix" to fetch programs.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-20">Active</TableHead>
                        <TableHead className="w-32">Course ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead className="w-80">Wix Program ID</TableHead>
                        <TableHead className="w-20">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mappings?.help_institute?.map((course, index) => (
                        <TableRow key={course.course_id || index} className={`hover:bg-muted/50 transition-colors ${!course.is_active ? 'opacity-60' : ''}`}>
                          <TableCell>
                            <Switch
                              checked={course.is_active || false}
                              onCheckedChange={(checked) => handleMappingChange('help_institute', index, 'is_active', checked)}
                              data-testid={`help-course-${course.course_id}-active`}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {course.course_id}
                          </TableCell>
                          <TableCell>
                            <div>
                              <span className="font-medium">{course.name}</span>
                              {course.description && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{course.description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              placeholder="Enter Wix Program ID"
                              value={course.wix_program_id || ''}
                              onChange={(e) => handleMappingChange('help_institute', index, 'wix_program_id', e.target.value)}
                              className="font-mono text-xs"
                              data-testid={`help-course-${course.course_id}-wix-id`}
                            />
                          </TableCell>
                          <TableCell>
                            {course.wix_program_id ? (
                              <Badge variant="default" className="bg-green-600">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Linked
                              </Badge>
                            ) : (
                              <Badge variant="secondary">Pending</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
            </Tabs>

            {/* Save Button */}
            <div className="flex justify-end mt-6 pt-4 border-t gap-3">
              <Button
                variant="outline"
                onClick={refreshFromWix}
                disabled={refreshing || !connectionStatus?.connection?.online_programs_api}
                data-testid="refresh-wix-programs-bottom-btn"
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh from Wix
              </Button>
              <Button
                onClick={saveMappings}
                disabled={saving}
                data-testid="save-wix-mappings-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Mappings
              </Button>
            </div>

            {/* Last Updated */}
            {(mappings?.updated_at || mappings?.fetched_at) && (
              <p className="text-xs text-muted-foreground mt-2 text-right">
                {mappings?.fetched_at && (
                  <span>Fetched from Wix: {new Date(mappings.fetched_at).toLocaleString()}</span>
                )}
                {mappings?.fetched_at && mappings?.updated_at && <span> | </span>}
                {mappings?.updated_at && (
                  <span>Last saved: {new Date(mappings.updated_at).toLocaleString()}</span>
                )}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Wix Enrollment Backfill Tool */}
        <Card data-testid="wix-backfill-tool-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Enrollment Backfill Tool
            </CardTitle>
            <CardDescription>
              Retrieves all Site Members enrolled in Online Programs (Students), then matches them to MYHELP participants by email and populates the SNAP E&T Program Report Card.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Run Backfill Button */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border">
              <div>
                <h4 className="font-medium">Run Enrollment Backfill</h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Fetches all Online Program participants from Wix, gets their email addresses, and matches to MYHELP SNAP E&T cases.
                </p>
              </div>
              <Button
                onClick={runBackfill}
                disabled={backfillRunning || !connectionStatus?.connection?.fully_connected}
                data-testid="run-backfill-btn"
              >
                {backfillRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Running Backfill...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Run Backfill
                  </>
                )}
              </Button>
            </div>

            {/* Backfill not available warning */}
            {!connectionStatus?.connection?.fully_connected && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    Backfill requires Members API and Online Programs API to be connected.
                  </p>
                </div>
              </div>
            )}

            {/* Backfill Results */}
            {backfillResults && (
              <div className="space-y-4 border-t pt-4">
                <h4 className="font-medium">Backfill Results</h4>
                
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
                  <div className="p-3 bg-muted/30 rounded-lg text-center">
                    <p className="text-2xl font-bold">{backfillResults.total_wix_students || backfillResults.total_wix_contacts || 0}</p>
                    <p className="text-xs text-muted-foreground">Wix Students</p>
                  </div>
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center border border-green-200 dark:border-green-800">
                    <p className="text-2xl font-bold text-green-600">{backfillResults.matched_and_updated?.length || 0}</p>
                    <p className="text-xs text-green-700 dark:text-green-400">Matched & Updated</p>
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center border border-blue-200 dark:border-blue-800">
                    <p className="text-2xl font-bold text-blue-600">{backfillResults.already_matched?.length || 0}</p>
                    <p className="text-xs text-blue-700 dark:text-blue-400">Already Matched</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center border border-gray-200 dark:border-gray-700">
                    <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{backfillResults.no_myhelp_participant?.length || 0}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">No MYHELP Match</p>
                  </div>
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center border border-amber-200 dark:border-amber-800">
                    <p className="text-2xl font-bold text-amber-600">{backfillResults.no_snap_et_case?.length || 0}</p>
                    <p className="text-xs text-amber-700 dark:text-amber-400">No SNAP E&T Case</p>
                  </div>
                  <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center border border-purple-200 dark:border-purple-800">
                    <p className="text-2xl font-bold text-purple-600">{backfillResults.no_email?.length || 0}</p>
                    <p className="text-xs text-purple-700 dark:text-purple-400">No Email</p>
                  </div>
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center border border-red-200 dark:border-red-800">
                    <p className="text-2xl font-bold text-red-600">{backfillResults.errors?.length || 0}</p>
                    <p className="text-xs text-red-700 dark:text-red-400">Errors</p>
                  </div>
                </div>

                {/* Timestamps */}
                <p className="text-xs text-muted-foreground">
                  Started: {backfillResults.started_at ? new Date(backfillResults.started_at).toLocaleString() : 'N/A'}
                  {backfillResults.completed_at && ` | Completed: ${new Date(backfillResults.completed_at).toLocaleString()}`}
                </p>

                {/* Matched & Updated Records */}
                {backfillResults.matched_and_updated?.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="font-medium text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Matched & Updated ({backfillResults.matched_and_updated.length})
                    </h5>
                    <div className="max-h-48 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Wix Contact</TableHead>
                            <TableHead>MYHELP Participant</TableHead>
                            <TableHead>Case ID</TableHead>
                            <TableHead>Wix Program</TableHead>
                            <TableHead>Progress</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {backfillResults.matched_and_updated.map((record, idx) => (
                            <TableRow key={idx} className="hover:bg-muted/50">
                              <TableCell className="text-xs">{record.contact_email}</TableCell>
                              <TableCell className="text-xs">{record.participant_name}</TableCell>
                              <TableCell className="text-xs font-mono">{record.case_id}</TableCell>
                              <TableCell className="text-xs">{record.wix_program_name || record.program_type}</TableCell>
                              <TableCell className="text-xs">{record.progress_percentage}%</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Already Matched Records */}
                {backfillResults.already_matched?.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="font-medium text-sm text-blue-700 dark:text-blue-400 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Already Matched - Skipped ({backfillResults.already_matched.length})
                    </h5>
                    <div className="max-h-32 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Wix Contact</TableHead>
                            <TableHead>MYHELP Participant</TableHead>
                            <TableHead>Case ID</TableHead>
                            <TableHead>Progress</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {backfillResults.already_matched.map((record, idx) => (
                            <TableRow key={idx} className="hover:bg-muted/50">
                              <TableCell className="text-xs">{record.contact_email}</TableCell>
                              <TableCell className="text-xs">{record.participant_name}</TableCell>
                              <TableCell className="text-xs font-mono">{record.case_id}</TableCell>
                              <TableCell className="text-xs">{record.existing_progress || 0}%</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* No MYHELP Participant */}
                {backfillResults.no_myhelp_participant?.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="font-medium text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      No MYHELP Match ({backfillResults.no_myhelp_participant.length})
                    </h5>
                    <div className="max-h-32 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Wix Member Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Enrollments</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {backfillResults.no_myhelp_participant.slice(0, 20).map((record, idx) => (
                            <TableRow key={idx} className="hover:bg-muted/50">
                              <TableCell className="text-xs">{record.member_name || record.contact_name || 'Unknown'}</TableCell>
                              <TableCell className="text-xs">{record.member_email || record.contact_email}</TableCell>
                              <TableCell className="text-xs">{record.enrollments_count || 0}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {backfillResults.no_myhelp_participant.length > 20 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          ... and {backfillResults.no_myhelp_participant.length - 20} more
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* No SNAP E&T Case */}
                {backfillResults.no_snap_et_case?.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="font-medium text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      No SNAP E&T Case ({backfillResults.no_snap_et_case.length})
                    </h5>
                    <div className="max-h-32 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Wix Contact</TableHead>
                            <TableHead>MYHELP Participant</TableHead>
                            <TableHead>Reason</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {backfillResults.no_snap_et_case.map((record, idx) => (
                            <TableRow key={idx} className="hover:bg-muted/50">
                              <TableCell className="text-xs">{record.contact_email}</TableCell>
                              <TableCell className="text-xs">{record.myhelp_participant_name}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{record.reason}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* No Wix Enrollments */}
                {backfillResults.no_wix_enrollments?.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="font-medium text-sm text-purple-700 dark:text-purple-400 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      No Wix Enrollments ({backfillResults.no_wix_enrollments.length})
                    </h5>
                    <div className="max-h-32 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>MYHELP Participant</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Case ID</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {backfillResults.no_wix_enrollments.map((record, idx) => (
                            <TableRow key={idx} className="hover:bg-muted/50">
                              <TableCell className="text-xs">{record.myhelp_participant_name}</TableCell>
                              <TableCell className="text-xs">{record.contact_email}</TableCell>
                              <TableCell className="text-xs font-mono">{record.case_id}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Errors */}
                {backfillResults.errors?.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="font-medium text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
                      <XCircle className="h-4 w-4" />
                      Errors ({backfillResults.errors.length})
                    </h5>
                    <div className="max-h-32 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Contact</TableHead>
                            <TableHead>Error</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {backfillResults.errors.map((record, idx) => (
                            <TableRow key={idx} className="hover:bg-muted/50">
                              <TableCell className="text-xs">{record.contact_email || record.contact_id || 'N/A'}</TableCell>
                              <TableCell className="text-xs text-red-600">{record.error}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sync History Section */}
        <Card data-testid="wix-sync-history-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Sync History
                </CardTitle>
                <CardDescription>
                  Automated progress sync logs and module advancements
                </CardDescription>
              </div>
              <Button
                onClick={triggerManualSync}
                disabled={manualSyncRunning || !connectionStatus?.connected}
                data-testid="manual-sync-btn"
              >
                {manualSyncRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Manual Sync
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Sync Configuration */}
            {syncConfig && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Schedule</p>
                  <p className="font-medium text-sm">{syncConfig.schedule}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Next Run</p>
                  <p className="font-medium text-sm">
                    {syncConfig.next_scheduled_run 
                      ? formatSyncDate(syncConfig.next_scheduled_run) 
                      : 'Not scheduled'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Module Advancement</p>
                  <Badge variant={syncConfig.module_advancement?.enabled ? "success" : "secondary"}>
                    {syncConfig.module_advancement?.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Max Retries</p>
                  <p className="font-medium text-sm">{syncConfig.max_retries}</p>
                </div>
              </div>
            )}

            {/* Sync Logs Table */}
            {syncHistory.length > 0 ? (
              <div className="space-y-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-center">Synced</TableHead>
                      <TableHead className="text-center">Failed</TableHead>
                      <TableHead className="text-center">Advancements</TableHead>
                      <TableHead>Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {syncHistory.map((log) => (
                      <React.Fragment key={log.id}>
                        <TableRow 
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleLogExpanded(log.id)}
                        >
                          <TableCell>
                            {expandedLogs[log.id] ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatSyncDate(log.started_at)}
                            {log.is_monday && (
                              <Badge variant="outline" className="ml-2 text-xs">Monday</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={log.type === 'nightly_sync' ? 'default' : 'secondary'}>
                              {log.type === 'nightly_sync' ? 'Nightly' : log.type === 'manual_single' ? 'Single' : 'Manual'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="text-green-600 font-medium">{log.synced_successfully || 0}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            {(log.failed || 0) > 0 ? (
                              <span className="text-red-600 font-medium">{log.failed}</span>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {(log.module_advancements?.length || 0) > 0 ? (
                              <span className="text-blue-600 font-medium">{log.module_advancements.length}</span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {log.duration_seconds ? `${log.duration_seconds.toFixed(1)}s` : '-'}
                          </TableCell>
                        </TableRow>
                        
                        {/* Expanded Details */}
                        {expandedLogs[log.id] && (
                          <TableRow>
                            <TableCell colSpan={7} className="bg-muted/20 p-4">
                              <div className="space-y-4">
                                {/* Summary */}
                                <div className="grid grid-cols-3 gap-4 text-sm">
                                  <div>
                                    <p className="text-muted-foreground">Total Cases</p>
                                    <p className="font-medium">{log.total_cases || 0}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Completed</p>
                                    <p className="font-medium">{formatSyncDate(log.completed_at)}</p>
                                  </div>
                                  <div>
                                    <p className="text-muted-foreground">Flagged for Review</p>
                                    <p className="font-medium">{log.flagged_for_review?.length || 0}</p>
                                  </div>
                                </div>
                                
                                {/* Module Advancements */}
                                {log.module_advancements?.length > 0 && (
                                  <div>
                                    <p className="font-medium text-sm mb-2 flex items-center gap-2">
                                      <GraduationCap className="h-4 w-4 text-blue-600" />
                                      Module Advancements
                                    </p>
                                    <div className="space-y-1">
                                      {log.module_advancements.map((adv, idx) => (
                                        <div key={idx} className="text-xs bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                                          <span className="font-medium">{adv.case_id}</span>: 
                                          {adv.all_modules_complete 
                                            ? ' All 8 modules complete! 🎉'
                                            : ` M${adv.completed_module} → M${adv.next_module}`
                                          }
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Module Completions */}
                                {log.module_completions?.length > 0 && (
                                  <div>
                                    <p className="font-medium text-sm mb-2 flex items-center gap-2">
                                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                                      Module Completions Detected
                                    </p>
                                    <div className="space-y-1">
                                      {log.module_completions.map((comp, idx) => (
                                        <div key={idx} className="text-xs bg-green-50 dark:bg-green-900/20 p-2 rounded">
                                          <span className="font-medium">{comp.participant_name}</span> - {comp.progress_percentage}% complete
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Errors */}
                                {log.errors?.length > 0 && (
                                  <div>
                                    <p className="font-medium text-sm mb-2 flex items-center gap-2 text-red-600">
                                      <AlertCircle className="h-4 w-4" />
                                      Errors ({log.errors.length})
                                    </p>
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                      {log.errors.map((err, idx) => (
                                        <div key={idx} className="text-xs bg-red-50 dark:bg-red-900/20 p-2 rounded">
                                          <span className="font-medium">{err.case_id || err.participant_name}</span>: {err.error}
                                          {err.retry_count > 0 && (
                                            <span className="text-muted-foreground ml-2">(retried {err.retry_count}x)</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Flagged for Review */}
                                {log.flagged_for_review?.length > 0 && (
                                  <div>
                                    <p className="font-medium text-sm mb-2 flex items-center gap-2 text-yellow-600">
                                      <AlertTriangle className="h-4 w-4" />
                                      Flagged for Review ({log.flagged_for_review.length})
                                    </p>
                                    <div className="space-y-1">
                                      {log.flagged_for_review.map((flagged, idx) => (
                                        <div key={idx} className="text-xs bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded">
                                          <span className="font-medium">{flagged.case_id}</span>: {flagged.error}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No sync history available yet</p>
                <p className="text-sm">The nightly sync runs at 2:00 AM Central</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Phase 6: Completion Detection Settings */}
        <Card data-testid="wix-completion-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-green-600" />
              Completion Detection Settings
            </CardTitle>
            <CardDescription>
              Configure automatic detection and notifications when participants complete Wix courses
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Enable Detection</Label>
                  <p className="text-xs text-muted-foreground">
                    Automatically detect when courses reach 100%
                  </p>
                </div>
                <Switch
                  checked={completionSettings.enabled}
                  onCheckedChange={(checked) => setCompletionSettings({...completionSettings, enabled: checked})}
                  data-testid="completion-detection-enabled"
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Auto Case Note</Label>
                  <p className="text-xs text-muted-foreground">
                    Create case note when completion detected
                  </p>
                </div>
                <Switch
                  checked={completionSettings.auto_create_case_note}
                  onCheckedChange={(checked) => setCompletionSettings({...completionSettings, auto_create_case_note: checked})}
                  data-testid="completion-auto-note"
                />
              </div>
              
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Notify Case Manager</Label>
                  <p className="text-xs text-muted-foreground">
                    Email case manager on completion
                  </p>
                </div>
                <Switch
                  checked={completionSettings.notify_case_manager}
                  onCheckedChange={(checked) => setCompletionSettings({...completionSettings, notify_case_manager: checked})}
                  data-testid="completion-notify-cm"
                />
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button 
                onClick={saveCompletionSettings} 
                disabled={completionSettingsLoading}
                data-testid="save-completion-settings-btn"
              >
                {completionSettingsLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Settings
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Phase 6: Completions Report */}
        <Card data-testid="wix-completions-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Course Completions
            </CardTitle>
            <CardDescription>
              View detected Wix course completions and auto-generated case notes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Date Range Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={completionFilters.start_date}
                  onChange={(e) => setCompletionFilters({...completionFilters, start_date: e.target.value})}
                  data-testid="completions-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={completionFilters.end_date}
                  onChange={(e) => setCompletionFilters({...completionFilters, end_date: e.target.value})}
                  data-testid="completions-end-date"
                />
              </div>
              <div className="space-y-2">
                <Label>&nbsp;</Label>
                <Button 
                  className="w-full"
                  onClick={fetchCompletions}
                  disabled={completionsLoading || !completionFilters.start_date || !completionFilters.end_date}
                  data-testid="fetch-completions-btn"
                >
                  {completionsLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Load Completions
                </Button>
              </div>
              <div className="space-y-2">
                <Label>&nbsp;</Label>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={exportCompletionsCsv}
                  disabled={!completions}
                  data-testid="export-completions-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>

            {/* Completions Results */}
            {completions && (
              <>
                {/* Summary */}
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                      <Trophy className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                        {completions.total_completions}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Total Completions ({completionFilters.start_date} to {completionFilters.end_date})
                      </p>
                    </div>
                  </div>
                </div>

                {/* Completions by Course */}
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">Completions by Course</h4>
                  {Object.keys(completions.completions_by_course || {}).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Trophy className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No completions detected in this date range</p>
                    </div>
                  ) : (
                    Object.entries(completions.completions_by_course).map(([courseName, courseData]) => (
                      <div key={courseName} className="border rounded-lg overflow-hidden">
                        <button
                          className="w-full flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 transition-colors"
                          onClick={() => toggleCourseExpanded(courseName)}
                          data-testid={`completions-course-toggle-${courseName.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                              <span className="font-bold text-green-600 text-sm">{courseData.count}</span>
                            </div>
                            <span className="font-medium text-sm">{courseName}</span>
                          </div>
                          {expandedCourses[courseName] ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                        
                        {expandedCourses[courseName] && (
                          <div className="p-3 border-t">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Participant</TableHead>
                                  <TableHead>Completion Date</TableHead>
                                  <TableHead>Case ID</TableHead>
                                  <TableHead>Detected At</TableHead>
                                  <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {courseData.completions.map((comp, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="font-medium">{comp.participant_name}</TableCell>
                                    <TableCell>{comp.completion_date}</TableCell>
                                    <TableCell>
                                      <Badge variant="outline">{comp.case_id}</Badge>
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                      {new Date(comp.detected_at).toLocaleString()}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Link to={`/cases/${comp.case_id}`}>
                                        <Button variant="ghost" size="sm">
                                          <Eye className="h-4 w-4 mr-1" />
                                          View Case
                                        </Button>
                                      </Link>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}

            {!completions && !completionsLoading && (
              <div className="text-center py-8 text-muted-foreground">
                <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a date range and click "Load Completions" to view detected course completions</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default WixIntegrationPage;
